import React, { useEffect, useState } from 'react';
import './HorizontalTabNavigation.scss';

export type HorizontalTabNavigationPanelProps = {
  id: string;
  label: React.ReactNode;
  children: React.ReactNode;
};

export const HorizontalTabPanel = ({ children }: HorizontalTabNavigationPanelProps) => (
  <>{children}</>
);

type HorizontalTabNavigationProps = {
  children: React.ReactNode;
  defaultActiveId?: string;
  activeId?: string;
  onActiveIdChange?: (id: string) => void;
  className?: string;
};

const HorizontalTabNavigation = ({
  children,
  defaultActiveId,
  activeId: controlledActiveId,
  onActiveIdChange,
  className = '',
}: HorizontalTabNavigationProps) => {
  const panels = React.Children.toArray(children).filter(
    React.isValidElement
  ) as React.ReactElement<HorizontalTabNavigationPanelProps>[];
  const firstPanelId = panels[0]?.props.id;
  const [internalActiveId, setInternalActiveId] = useState(defaultActiveId || firstPanelId);
  const activeId = controlledActiveId ?? internalActiveId;

  useEffect(() => {
    if (!panels.some(panel => panel.props.id === activeId)) {
      setInternalActiveId(defaultActiveId || firstPanelId);
    }
  }, [activeId, defaultActiveId, firstPanelId, panels]);

  return (
    <div className={`horizontal-tab-navigation ${className}`.trim()}>
      <div className="horizontal-tab-navigation__list" role="tablist" aria-label="Sections">
        {panels.map(panel => {
          const isActive = panel.props.id === activeId;
          return (
            <button
              key={panel.props.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={`horizontal-tab-navigation__tab${isActive ? ' horizontal-tab-navigation__tab--active' : ''}`}
              onClick={() => {
                setInternalActiveId(panel.props.id);
                onActiveIdChange?.(panel.props.id);
              }}
            >
              {panel.props.label}
            </button>
          );
        })}
      </div>
      {panels.map(panel => (
        <div
          key={panel.props.id}
          role="tabpanel"
          hidden={panel.props.id !== activeId}
          className="horizontal-tab-navigation__panel"
        >
          {panel.props.children}
        </div>
      ))}
    </div>
  );
};

export default HorizontalTabNavigation;
